import { badgeClasses, Box, BoxProps, IconButton, styled } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { TooltipFeedbackProps } from "../../../feedback/TooltipFeedback"
import { TooltipFeedbackGroupProps } from "../../../feedback/TooltipFeedbackGroup"
import { focusStyle } from "../../../theme"
import { Variant } from "../../../types"

export const BasicDragContainerWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",

  ":not(:last-child)": {
    marginBottom: theme.spacing(2)
  }
}))

export const IndexWrapper = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[800],
  fontSize: theme.typography.body2.fontSize,
  marginRight: theme.spacing(2)
}))

export const ContentWrapper = styled(Box)<{ variant?: Variant }>(
  ({ theme, variant }) => ({
    overflow: "auto",
    width: "100%",
    letterSpacing: 0,
    padding: theme.spacing(2, 0, 2, 0),
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
    scrollbarWidth: "thin",
    msOverflowStyle: "auto",

    [theme.breakpoints.down("mobile")]: {
      overflow: "auto",
      fontSize: theme.typography.body2.fontSize,
      lineHeight: theme.typography.body2.lineHeight
    }
  })
)

export const RemoveWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  display: "flex",
  justifyContent: "flex-end",
  width: "30px",
  top: "50%",
  right: 0,
  transform: `translate(0, -50%)`
}))

interface RemoveButtonProps {
  selected: boolean
}

export const RemoveButton = styled(IconButton)<RemoveButtonProps>(
  ({ theme, selected }) => ({
    opacity: selected ? 1 : 0,

    ":focus-visible": {
      opacity: 1,
      color: "transparent",
      backgroundColor: `${theme.palette.blue[500] + " !important"}`
    },

    ":hover": {
      backgroundColor: theme.palette.blue[500]
    },

    [`${theme.breakpoints.down("mobile")}, (hover: none)`]: {
      visibility: selected ? "visible" : "hidden"
    }
  })
)

interface StyledDragContainerProps extends BoxProps {
  disabled?: boolean
  drag?: boolean
  assigned?: boolean
  placehold?: boolean
  selected?: boolean
  fluid?: boolean
  variant?: Variant
}
const STYLED_DRAG_CONTAINER_PROPS = [
  "disabled",
  "drag",
  "assigned",
  "placehold",
  "selected",
  "fluid",
  "variant"
]

export const StyledBasicDragContainer = styled(Box, {
  shouldForwardProp: (prop: string) =>
    !STYLED_DRAG_CONTAINER_PROPS.includes(prop)
})<StyledDragContainerProps>(
  ({
    theme,
    disabled,
    drag,
    assigned,
    placehold,
    selected,
    variant,
    fluid = false
  }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    wordWrap: "break-word",
    hyphens: "initial",
    padding: theme.spacing(2, 7, 2, 3),
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[2],
    border: "1px solid transparent",
    outline: "3px solid transparent",
    cursor: "pointer",
    userSelect: "none",
    position: "relative",

    "& p": {
      margin: "0 !important"
    },

    ...(disabled && {
      pointerEvents: "none",
      boxShadow: "none"
    }),

    "&:focus-visible": {
      ...((!disabled || !(selected && drag)) && {
        ...focusStyle(theme).border,

        ...(drag && {
          outline: "3px solid transparent",
          border: "1px solid transparent"
        }),

        "& button": {
          opacity: 1
        }
      })
    },

    ...(!assigned
      ? {
          opacity: drag && placehold ? 0 : 1,
          color: disabled ? theme.palette.grey[400] : theme.palette.grey[800],
          border: `${
            (selected || placehold) && !disabled ? "2px" : "1px"
          } solid ${placehold ? "transparent" : theme.palette.blue[300]}`,
          borderColor:
            disabled || drag ? theme.palette.grey[50] : theme.palette.blue[300],
          backgroundColor: "white",

          ...((selected || placehold) && {
            backgroundColor: theme.palette.blue[100]
          }),

          ...((disabled || drag) && {
            backgroundColor: theme.palette.grey[50]
          }),

          ...(drag && !disabled && { boxShadow: "none" }),

          ...(placehold &&
            !disabled && {
              boxShadow: theme.shadows[6]
            }),

          ...(!disabled &&
            !selected &&
            !drag &&
            !placehold && {
              "&:hover": {
                [theme.breakpoints.up("mobile")]: {
                  backgroundColor: theme.palette.blue[50]
                }
              }
            })
        }
      : {
          color: drag ? "transparent" : "white",
          backgroundColor: drag
            ? theme.palette.blue[50]
            : theme.palette.blue[600],
          border:
            selected && !drag
              ? `2px solid ${theme.palette.blue[200]}`
              : `2px dashed ${
                  drag ? theme.palette.grey[500] : theme.palette.grey[100]
                }`,
          boxShadow: selected && !drag ? theme.shadows[6] : "none",
          backgroundClip: "padding-box",

          "& p": {
            color: drag ? "transparent" : "white",
            margin: "0 !important"
          },

          ...(variant &&
            variant === Variant.correct && {
              backgroundColor: theme.palette.lightGreen[700],
              borderColor: theme.palette.lightGreen[300],
              position: "relative"
            }),

          ...(variant &&
            variant === Variant.invalid && {
              backgroundColor: theme.palette.orange[700],
              borderColor: theme.palette.orange[300],
              position: "relative"
            }),

          ...(variant &&
            variant === Variant.neutral && {
              backgroundColor: theme.palette.blue["A800"],
              borderColor: theme.palette.blue[300],
              position: "relative"
            }),

          "&:hover": {
            [theme.breakpoints.up("mobile")]: {
              backgroundColor: theme.palette.blue[500],

              button: {
                opacity: 1
              }
            }
          }
        }),

    ...(!fluid && { width: "150px", flexGrow: 1 })
  })
)

export const TimesCircleIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.common.white
}))

export const StyledReviewBadge = styled(Box)<
  TooltipFeedbackProps | TooltipFeedbackGroupProps
>(({ theme }) => ({
  [`& .${badgeClasses.anchorOriginTopLeftRectangular}`]: {
    [theme.breakpoints.down("mobile")]: {
      left: "2px",
      top: "4px"
    }
  }
}))

export const VariantIconContainer = styled(Box)<{ variant?: string }>(
  ({ theme, variant }) => ({
    position: "absolute",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    border: `1px solid ${
      variant === "correct"
        ? theme.palette.lightGreen[300]
        : theme.palette.orange[300]
    }`,
    background:
      variant === "correct"
        ? theme.palette.lightGreen[700]
        : theme.palette.orange[700],
    top: theme.spacing(-1),
    left: theme.spacing(-1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  })
)

export const VariantIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  fontSize: "8px",
  color: theme.palette.common.white
}))
